import { MainNavScreens, LeftNavStyle } from "breadcommon";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RyeIcon } from "../rye/RyeIcon";
import { RyeTooltip } from "../rye/RyeTooltip";
import { UrlContext } from "../UrlContext";
import { buildClasses } from "../utils/buildClasses";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { LoadingInitialDataContext } from "../LoadUser";
import { useOpenSettings } from "../settings_screen/settingsDefinitions";
import { MAIN_NAV_DEFINITIONS, mainNavPath } from "./mainNavDefinitions";
import { navRiseDelay } from "../rye/riseIn";

export function MainNavButton({
  mainNavScreen,
}: {
  mainNavScreen: MainNavScreens;
}): JSX.Element {
  const { label, icon } = MAIN_NAV_DEFINITIONS[mainNavScreen];
  const urlInfo = useContext(UrlContext);
  const userPrefs = useContext(PreferencesContext);
  const loadingInitialData = useContext(LoadingInitialDataContext);
  const navigate = useNavigate();

  // As the app first appears, the selected item waits for the nav to fade in,
  // then selects with the same animation as a click.
  const [selectionDelay] = useState(navRiseDelay);
  const [holdSelection, setHoldSelection] = useState(selectionDelay !== null);
  useEffect(() => {
    if (selectionDelay === null) return;
    const timer = setTimeout(() => setHoldSelection(false), selectionDelay);
    return () => clearTimeout(timer);
  }, [selectionDelay]);

  // Settings opens over the current screen, which stays selected.
  const isSelected =
    !holdSelection &&
    !loadingInitialData.preferences &&
    (urlInfo.currentTab ?? userPrefs.web.home_screen) === mainNavScreen;

  return (
    <MainNavButtonBase
      iconName={icon}
      // The home glyph has a smaller visual footprint at the same font size.
      iconClassName={mainNavScreen === MainNavScreens.HOME ? "scale-110" : ""}
      label={label}
      isSelected={isSelected}
      onClick={() => navigate(mainNavPath(mainNavScreen))}
    />
  );
}

export function MainNavSettingsButton(): JSX.Element {
  const openSettings = useOpenSettings();
  return (
    <MainNavButtonBase
      iconName="settings"
      label="Settings"
      isSelected={false}
      onClick={() => openSettings("accounts")}
    />
  );
}

function MainNavButtonBase({
  iconName,
  iconClassName = "",
  label,
  isSelected,
  onClick,
}: {
  iconName: string;
  iconClassName?: string;
  label: string;
  isSelected: boolean;
  onClick: () => void;
}): JSX.Element {
  const userPrefs = useContext(PreferencesContext);
  const navStyle = userPrefs.web.left_nav_style;

  const button = (
    <div
      className={buildClasses(
        "relative",
        "isolate", // keeps the tab's -z-10 behind this item's content only
        "flex",
        "items-center",
        "gap-3",
        "p-2",
        "cursor-pointer",
        "select-none",
        {
          if: isSelected,
          then: "text-purple",
          else: buildClasses(
            "text-on-surface-500",
            "hover:text-on-surface-800",
          ),
        },
      )}
      onClick={onClick}
    >
      {/* The selected item sits on a small raised tab, styled like a panel.
      It's always rendered so it can rise out of the background on selection,
      and sink back into it when another item is chosen. */}
      <span
        aria-hidden
        className={buildClasses(
          "absolute",
          "inset-0",
          "-z-10",
          "rounded-lg",
          "border",
          "bg-surface",
          // The main panel's warm outline.
          "border-edge/[0.12]",
          "border-t-edge/[0.08]",
          "border-b-edge/[0.2]",
          "transition-[opacity,transform,box-shadow]",
          "duration-300",
          "ease-[cubic-bezier(0.34,1.4,0.64,1)]",
          "motion-reduce:transition-none",
          {
            if: isSelected,
            then: buildClasses(
              "opacity-100",
              "scale-100",
              "shadow-main-content",
            ),
            else: buildClasses(
              "opacity-0",
              "scale-[0.92]",
              "translate-y-px",
              "shadow-none",
            ),
          },
        )}
      />
      <RyeIcon
        name={iconName}
        fill={isSelected}
        size={"custom"}
        className={buildClasses(
          "text-[1.2rem]",
          { if: isSelected, then: "motion-safe:animate-pop" },
          iconClassName,
        )}
      />
      <NavLabel navStyle={navStyle} text={label.toLowerCase()} />
    </div>
  );

  if (navStyle === LeftNavStyle.ALWAYS_COLLAPSED) {
    return (
      <RyeTooltip text={label} placement="right" delay={200}>
        {button}
      </RyeTooltip>
    );
  }

  return button;
}

export function NavLabel({
  navStyle,
  text,
}: {
  navStyle: LeftNavStyle;
  text: string;
}): JSX.Element | null {
  if (navStyle === LeftNavStyle.ALWAYS_COLLAPSED) {
    return null;
  }

  return (
    <span
      className={buildClasses("text-sm", "whitespace-nowrap", "capitalize", {
        if: navStyle === LeftNavStyle.EXPAND_ON_HOVER,
        then: buildClasses(
          "opacity-0",
          "group-hover/mainnav:opacity-100",
          "group-data-[expanded]/mainnav:opacity-100",
          "transition-opacity",
          "duration-200",
        ),
      })}
    >
      {text}
    </span>
  );
}
